import { Outlet, Link } from 'react-router-dom';
import { useAuth } from "../../context/AuthContext.tsx";
import { Fragment, useState, useMemo } from 'react';
import {
    User,
    LogOut,
    ChevronDown,
    LayoutDashboard,
    CreditCard,
    Headphones,
    FileText,
    Menu as MenuIcon,
    X,
    Link as LinkIcon, User2Icon, FormInput, DoorClosed,
    Search,
} from 'lucide-react';
import { Menu, MenuItems, MenuItem, MenuButton, Dialog, Transition, TransitionChild, DialogPanel } from '@headlessui/react';
import { useAuthApi } from "../../context/ApiContext.tsx";
import { UserType } from "../../api/ApiTypes.ts";
import {NavLinks, NavigationItem} from './NavLinks'; // Import the type-safe NavLinks component
// Import the logo image
import stewartLogo from '../../assets/stewart_logo.png'
import HelpGuide from '../HelpGuide.tsx';

/**
 * AuthLayout Component - Main layout component for authenticated pages
 * Includes top navigation bar, sidebar, mobile menu, and footer
 */
export const AuthLayout =  () => {
    const { me, setMe } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
    const authApi = useAuthApi();

    // Define navigation items based on user type
    const navigation: NavigationItem[] = useMemo(() => {

        // Appraiser-specific navigation items
        if (me?.userType === UserType.APPRAISER) {
            return [
                { name: 'Dashboard', href: '/appraiser/dashboard', icon: LayoutDashboard },
                { name: 'Payments', href: '/appraiser/payments', icon: CreditCard },
                { name: 'Secure Links', href: '/appraiser/secure-links', icon: LinkIcon },
                { name: 'Quotes', href: '/appraiser/quotes', icon: FileText },
                { name: 'Support', href: '/appraiser/support', icon: Headphones },
            ];
        }
        if (me?.userType === UserType.APPRAISER_ADMIN) {
            return [
                { name: 'Dashboard', href: '/appraiser/dashboard', icon: LayoutDashboard },
                { name: 'Appraisers', href: '/appraiser-admin/appraisers', icon: User2Icon },
                { name: 'Payments', href: '/appraiser/payments', icon: CreditCard },
                { name: 'Secure Links', href: '/appraiser/secure-links', icon: LinkIcon },
                { name: 'Quotes', href: '/appraiser/quotes', icon: FileText },
                { name: 'Support', href: '/appraiser/support', icon: Headphones },
            ];
        }

        // Staff-specific navigation items
        if (me?.userType === UserType.STAFF) {
            return [
                { name: 'Dashboard', href: '/staff/dashboard', icon: LayoutDashboard },
                { name: 'Order Search', href: '/staff/order-search', icon: Search },
            ];
        }

        if (me?.userType === UserType.SECRET) {
            return [
                { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
                { name: 'Backdoor', href: '/admin/backdoor-login', icon: DoorClosed },
                { name: 'Forms', href: '/admin/forms', icon: FormInput },
                { name: 'CA Captures', href: '/admin/ca-captures', icon: FileText },
                { name: 'FormsV2', href: '/admin/formsv2', icon: FormInput },
                { name: 'Manage Profiles', href: '/admin/manage-profiles', icon: User}
            ];
        }

        // Fallback to empty array if user type is unknown
        return [];
    }, [me?.userType]);

    // Format user's full name from firstName and lastName, fallback to 'User'
    const fullName = [me?.firstName, me?.lastName]
        .filter(Boolean)
        .join(' ') || 'User';

    // Handle user logout with error handling
    const handleLogout = async (): Promise<void> => {
        try {
            await authApi.logout();
            setMe(null);
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    return (
        // Main container with full height and background color
        <div className="min-h-screen flex flex-col bg-slate-50">
            {/* Top Navigation Bar - Fixed position with shadow and border */}
            <nav className="bg-white shadow-sm border-b border-slate-200 fixed top-0 left-0 right-0 z-50">
                <div className="w-full px-1 sm:px-4">
                    <div className="flex justify-between h-14 sm:h-16">
                        {/* Left section - Contains menu button and logo */}
                        <div className="flex items-center">
                            {/* Mobile menu button - Only visible on mobile */}
                            <div className="lg:hidden">
                                <button
                                    type="button"
                                    className="p-1 rounded-md text-slate-700 hover:bg-slate-100"
                                    onClick={() => setSidebarOpen(true)}
                                >
                                    <MenuIcon className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Logo and Brand - Now uses image and links to root */}
                            <Link to="/" className="flex items-center pl-1">
                                <img
                                    src={stewartLogo}
                                    alt="Stewart Logo"
                                    className="h-8 w-auto sm:h-10"
                                />
                            </Link>
                        </div>

                        {/* Right section - Contains user dropdown menu */}
                        <div className="flex items-center">
                            <Menu as="div" className="relative inline-block text-left pr-1 sm:pr-0">
                                {({ open }) => (
                                    <>
                                        {/* User menu dropdown button */}
                                        <MenuButton
                                            className="flex items-center space-x-1 px-2 py-1 text-xs sm:text-sm font-medium text-slate-700
                                                     hover:bg-slate-50 rounded-md transition-colors duration-200"
                                        >
                                            <span className="hidden sm:block">{fullName}</span>
                                            <ChevronDown
                                                className={`h-4 w-4 transition-transform duration-200 
                                                ${open ? 'rotate-180' : ''}`}
                                            />
                                        </MenuButton>

                                        {/* Dropdown menu items */}
                                        <MenuItems
                                            className="absolute right-0 mt-2 w-40 sm:w-48 bg-white rounded-md shadow-lg
                                                     border border-slate-200 z-10 py-1 focus:outline-none"
                                        >
                                            <MenuItem>
                                                {({ focus }) => (
                                                    <Link
                                                        to={'/profile'}
                                                        className={`flex items-center px-4 py-2 text-xs sm:text-sm text-slate-700
                                                                 ${focus ? 'bg-slate-50' : ''} transition-colors duration-200`}
                                                    >
                                                        <User className="mr-2 h-4 w-4" />
                                                        Profile
                                                    </Link>
                                                )}
                                            </MenuItem>

                                            <MenuItem>
                                                {({ focus }) => (
                                                    <button
                                                        onClick={handleLogout}
                                                        className={`flex items-center w-full px-4 py-2 text-xs sm:text-sm text-red-600
                                                                 ${focus ? 'bg-red-50' : ''} transition-colors duration-200`}
                                                    >
                                                        <LogOut className="mr-2 h-4 w-4" />
                                                        Logout
                                                    </button>
                                                )}
                                            </MenuItem>
                                        </MenuItems>
                                    </>
                                )}
                            </Menu>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Mobile Navigation Drawer - Slides in from left on mobile */}
            <Transition show={sidebarOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
                    {/* Dark overlay behind the drawer */}
                    <TransitionChild
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-slate-600 bg-opacity-75" />
                    </TransitionChild>

                    <div className="fixed inset-0 z-50 flex">
                        {/* Mobile menu panel with slide animation */}
                        <TransitionChild
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <DialogPanel className="relative flex w-3/4 max-w-xs flex-1 flex-col bg-blue-900">
                                {/* Close button */}
                                <TransitionChild
                                    as={Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="absolute top-0 right-0 -mr-10 pt-2">
                                        <button
                                            type="button"
                                            className="flex h-8 w-8 items-center justify-center rounded-full focus:outline-none"
                                            onClick={() => setSidebarOpen(false)}
                                        >
                                            <X className="h-5 w-5 text-white" />
                                        </button>
                                    </div>
                                </TransitionChild>

                                {/* Mobile menu content */}
                                <div className="flex-1 overflow-y-auto pt-4 pb-4">
                                    <div className="flex-shrink-0 flex items-center px-4">
                                        <img
                                            src={stewartLogo}
                                            alt="Stewart Logo"
                                            className="h-6 w-auto"
                                        />
                                    </div>
                                    <nav className="mt-4 px-2 space-y-1">
                                        <NavLinks
                                            mobile
                                            onItemClick={() => setSidebarOpen(false)}
                                            navigation={navigation}
                                        />
                                    </nav>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </Dialog>
            </Transition>

            {/* Add a spacer to push content below the fixed navbar */}
            <div className="h-14 sm:h-16"></div>

            {/* Main content area with sidebar */}
            <div className="flex-1 flex relative">
                {/* Desktop Sidebar - Hidden on mobile, now fixed position with top offset */}
                <div className="hidden lg:block lg:w-64 fixed left-0 top-16 bottom-0 z-40">
                    <div className="h-full bg-blue-900 pt-5 overflow-y-auto">
                        <nav className="flex-1 px-4 space-y-1">
                            <NavLinks navigation={navigation} />
                        </nav>
                    </div>
                </div>

                {/* Main Content Area - Add left padding to compensate for fixed sidebar */}
                <main className="flex-1 overflow-x-auto p-2 sm:p-4 lg:pl-72 lg:p-6 min-w-0">
                    <div className="max-w-7xl mx-auto">
                        <Outlet />
                        <HelpGuide />
                    </div>
                </main>
            </div>

            {/* Footer - Sticky at bottom */}
            <footer className="bg-white border-t border-slate-200 py-2 sm:py-4">
                <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 lg:pl-72">
                    <p className="text-center text-xs sm:text-sm text-slate-500">
                        © {new Date().getFullYear()} Stewart Valuation Intelligence
                    </p>
                </div>
            </footer>
        </div>
    );
};